import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, useHistory, Switch, useLocation, Link } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useTracking } from '@modernatx/tracking';
import NewHomePage from "./pages/NewHomepage";
import GlobalStyle from "./global/GlobalStyle";
import { ExternalRouterPrompt, Spinner } from "../src/components";
import { Security } from "@okta/okta-react";
import { OktaAuth, toRelativeUrl, urlParamsToObject } from "@okta/okta-auth-js";
import { oidcConfig } from "./config";
import { IdxTransaction, UserInfoContext } from './contexts';
import Header from "./components/Header";
import Footer from "./components/Footer";
import { setLocale, setLocaleISOCode } from "./features/globalStates/globalStatesSlice";
import { clearGlobalMessageStore } from "./features/globalMessages/globalMessageSlice";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { loadGlobalMessage } from "./features/globalMessages/globalMessageThunkApi";
import Locale from "./Locale";
import LoginCallback from "./components/LoginCallback";
import { ModalProvider } from "./providers/ModalProvider";
import { USER_STATUS } from "./constants";
import FallbackErrorPage from "./pages/FallbackErrorPage";
import { socialLink } from "./data/externalLinkData";
import {FeatureFlagProvider, useFeatureFlag} from "./context/FeatureFlagContext";
import AuthHomepage from "@/pages/AuthHomepage";

const oktaAuth = (() => {
  const { state } = urlParamsToObject(window.location.search);
  return new OktaAuth(
      Object.assign({}, oidcConfig, {
        state,
      })
  );
})();
const App = () => {
  const [appInitialized, setAppInitialized] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const relativePathname = location?.pathname;
  const [processing, setProcessing] = useState(false);
  const dispatch = useDispatch()
  const [transaction, setTransaction] = useState(null);
  const [showNavigationAlert, setShowNavigationAlert] = useState(false);
  const [externalLink, setExternalLink] = useState("");
  const [externalLinkModalType, setExternalLinkModalType] = useState("socialLink");
  const locale = useSelector((state) => state.globalStates.locale);
  const userStatus = useSelector((state) => state.globalStates.userStatus);
  const captchaSiteKey = process.env.CAPTCHA_SITE_KEY;
  const externalLinkModalHeading = "Leaving Moderna Direct";
  const externalLinkModalContent = "You are now leaving Moderna Direct. The intended use and audience for the linked website may differ.";
  const externalLinkModalCheckBoxLabel = "Don't remind me again";
  const externalLinkModalButtonLabel = "Continue";
  const isAuthenticatedInvoicePayAvailable = useFeatureFlag("AUTHENTICATED_INVOICE_PAY");

  const { trackError } = useTracking();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const onAuthRequired = () => {
    history.push(`/${locale}/`);
  };

  const getUser = () => {
    const user = localStorage.getItem("currentUser")
    if (user !== "undefined")
      return JSON.parse(user)
    else
      return null
  }

  const [userInfo, setUserInfo] = useState(getUser()); //leave it here
  
  const prevPathnameRef = useRef("");
  useEffect(() => {
    history.listen((location) => {
      const currentPathname = location.pathname;
      // only scrollTo top if the pathname changes
      if (prevPathnameRef.current !== currentPathname) {
        window.scrollTo(0, 0);
      }
      prevPathnameRef.current = currentPathname;
    });
  }, [history]);

  useEffect(()=>{
    dispatch(clearGlobalMessageStore());
    dispatch(loadGlobalMessage());

    if ((!isCurrentUserAvailable() && !window.location.href.includes('login/callback'))) {
      dispatch(setLocale('en-US'));
      dispatch(setLocaleISOCode('US'));
    }

    if (localStorage.getItem("okta-token-storage") === "{}") {
      localStorage.removeItem("currentUser")
    }

    if (oktaAuth.isLoginRedirect()) {
      handleRedirect();
    }

    setAppInitialized(true);
  }, []);

  // Force onboarding users to stay on the page they are allowed access to
  useEffect(() => {
    if (userStatus !== USER_STATUS.ACTIVE) {
      switch (userStatus) {
        case USER_STATUS.VERIFIED:
          history.push(`/onboarding/contact-info`);
          break;
        case USER_STATUS.ONBOARDING_PERSON_COMPLETE:
          history.push(`/onboarding/contact-info`);
          break;
        case USER_STATUS.ONBOARDING_ORG_COMPLETE:
          history.push(`/onboarding/shipping-address`);
          break;
        case USER_STATUS.ONBOARDING_SHIP_COMPLETE:
          history.push(`/onboarding/billing-address`);
          break;
        case USER_STATUS.ONBOARDING_BILL_COMPLETE:
          history.push(`/onboarding/medical-identification`);
          break;
        default:
          localStorage.removeItem("currentUser");
      }
    }
  }, [userStatus]);

  useEffect(()=>{
    let newUrl = ''
    if(locale === 'en-US' && window.location.href.includes('en-US')){
      newUrl = window.location.href.replace(/\/en-US/, '');
    } else if(locale === 'en-US' && window.location.href.includes('en-DE') && isCurrentUserAvailable()){
      newUrl = window.location.href.replace(/\/en-DE/, '');
    } else if(locale === 'en-DE' && window.location.href.includes('en-US') && isCurrentUserAvailable()){
      newUrl = window.location.href.replace(/\/en-US/, '/en-DE');
    }
    window.history.replaceState({}, '', newUrl);
  }, [history.location.pathname, locale]);


  const handleRedirect = async () => {
    await oktaAuth.handleLoginRedirect();
  }

  const closeNavigationAlert = (type) => {
    setShowNavigationAlert(false)
  }

  const socialLinkMessage = () => {
    return socialLink.message.split('[PRIVACY_POLICY]').map((part, index, array) =>
        index === array.length - 1 ? (
            part
        ) : (
            <>
                {part}
                <Link to='/privacy-policy' target="_blank" rel="noopener noreferrer">
                    Privacy Statement
                </Link>
            </>
        )
    );
};

  const isCurrentUserAvailable = () => localStorage.getItem("currentUser") && localStorage.getItem("currentUser") !== "undefined";
  const isOnboarding = () => {
    const basePath = window.location.pathname;
    const isEmailVerificationOrConfirmation = basePath === `/${locale}/registration/email-verification` ||
        basePath === `/registration/email-verification` ||
        basePath === `/${locale}/registration/email-confirmation` ||
        basePath === `/registration/email-confirmation`;

    const isOnboardingPath = basePath === `/${locale}/onboarding` ||
        basePath === '/onboarding' ||
        basePath.includes('/onboarding');

    return isEmailVerificationOrConfirmation || (isCurrentUserAvailable() && isOnboardingPath);
  };

  const handleErrorBoundaryError = (error) => {
    // Reports to Dynatrace
    reportError(`User encountered error boundary and was shown the fallback page.${error}`);
    // Reports to Amplitude
    trackError(new Error(`User encountered error boundary and was shown the fallback page.${error}`))
  }

  return appInitialized ? (
      <GoogleReCaptchaProvider
          reCaptchaKey={captchaSiteKey}
          useEnterprise={true}
      >
        <div>
          <div>
            <Security
                oktaAuth={oktaAuth}
                restoreOriginalUri={restoreOriginalUri}
                onAuthRequired={onAuthRequired}>

              <Spinner processing={processing} />
              <FeatureFlagProvider>
                <IdxTransaction.Provider value={{ transaction, setTransaction }}>
                  <UserInfoContext.Provider value={{ userInfo, setUserInfo }}>
                    <ErrorBoundary FallbackComponent={FallbackErrorPage} onReset={() => history.push('/home')}
                                  onError={(err) => handleErrorBoundaryError(err)}>
                      <GlobalStyle />
                      {
                          externalLinkModalType === "extrenalLink" && showNavigationAlert && !localStorage.getItem('dontRemindMeExternal') && <ExternalRouterPrompt
                              isOpen={showNavigationAlert}
                              closeModalHandler={() => closeNavigationAlert("extrenalLink")}
                              externalLink={externalLink}
                              onOpenLink={setShowNavigationAlert}
                              type={"extrenalLink"}
                              heading= {externalLinkModalHeading}
                              headingStyle={{
                                fontFamily: "Aeonik Bold",
                                fontSize: "30px",
                                '@media screen and (max-width: 500px)': {
                                  fontSize: '24px',
                                  paddingBottom: "24px"
                                }
                              }}
                              content= {externalLinkModalContent}
                              checkBoxLabel= {externalLinkModalCheckBoxLabel}
                              buttonLabel= {externalLinkModalButtonLabel}
                          />
                      }

                      {
                          externalLinkModalType === "socialLink" && showNavigationAlert && !localStorage.getItem('dontRemindMe') &&
                          <ExternalRouterPrompt
                              isOpen={showNavigationAlert}
                              closeModalHandler={() => setShowNavigationAlert(false)}
                              externalLink={externalLink}
                              onOpenLink={setShowNavigationAlert}
                              heading={
                                <>
                                  {socialLink?.header} <h1 style={{ fontFamily: "Aeonik Bold" }}>{socialLink?.subHeader}</h1>
                                </>
                              }
                              modalCustomStyle={{
                                borderLeft: "24px solid #BFE6F6",
                                '@media screen and (max-width: 500px)': {
                                  borderLeft: 'unset',
                                  borderTop: "24px solid #BFE6F6",
                                  padding: "16px 16px 32px 32px"
                                }
                              }}
                              headingStyle={{
                                fontFamily: "Aeonik Light",
                                fontSize: "40px",
                                '@media screen and (max-width: 500px)': {
                                  fontSize: '24px',
                                  paddingBottom: "24px"
                                }
                              }}
                              content={
                                socialLinkMessage()
                              }
                              buttonLabel={socialLink?.buttonLabel}
                              checkBoxLabel={socialLink?.remindMeText}
                          />}
                      <ModalProvider>
                        {!isOnboarding() && <Header setProcessing={setProcessing}/>}
                      </ModalProvider>
                        <Switch>
                          <Route exact={true} path="/">
                            <ModalProvider>
                              {isAuthenticatedInvoicePayAvailable ? <AuthHomepage /> : <NewHomePage />}
                            </ModalProvider>
                          </Route>
                          <Route exact={true} path="/login/callback" component={LoginCallback} />
                          <Route path="/:locale">
                            <Locale relativePathname={relativePathname} locale={locale} setExternalLink={setExternalLink} setShowNavigationAlert={setShowNavigationAlert} setExternalLinkModalType={setExternalLinkModalType} isCurrentUserAvailable={isCurrentUserAvailable} />
                          </Route>
                        </Switch>
                      {!isOnboarding() && <Footer setShowNavigationAlert={setShowNavigationAlert} setExternalLink={setExternalLink} setExternalLinkModalType={setExternalLinkModalType} />}
                    </ErrorBoundary>
                  </UserInfoContext.Provider>
                </IdxTransaction.Provider>
              </FeatureFlagProvider>
            </Security>
          </div>
        </div>
      </GoogleReCaptchaProvider>
  ) : (
      <Spinner processing={!appInitialized} />
  );
};

export default App;
